import { GraphQLCameraFleetFlagRequester } from '@cobliteam/api-lib';
import type { FleetProducts } from '@cobliteam/common-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const FLEET_FLAG_QUERY_KEY = 'camera-fleet-flag';

const FLEET_PRODUCTS_QUERY_KEY = [FLEET_FLAG_QUERY_KEY, 'FLEET_PRODUCTS_FLAG'];

const useCameraFleetFlag = () => {
  const graphQLCameraFleetFlagRequester = new GraphQLCameraFleetFlagRequester();
  const queryClient = useQueryClient();

  const getCameraFleetFlag = (enabled = true) => {
    return useQuery<Boolean>({
      queryKey: [FLEET_FLAG_QUERY_KEY],
      queryFn: graphQLCameraFleetFlagRequester.getCameraFleetFlag,
      retry: false,
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const getFleetPremiumPackageFlag = (enabled = true) => {
    return useQuery<FleetProducts | undefined, unknown, boolean>({
      queryKey: FLEET_PRODUCTS_QUERY_KEY,
      queryFn: graphQLCameraFleetFlagRequester.getFleetProducts,
      select: (data: FleetProducts | undefined) => {
        return !!data?.products.some((product) => product.name.toLowerCase() === 'premium');
      },
      retry: false,
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const hasCameraProDMSEvents = (enabled = true) => {
    // theres no diference in camera package anymore, so we can always return true for enable pro DMS events
    return true;
  };

  /**
   * Dev-only override used by the developer-tools Flags panel to simulate a
   * premium fleet without touching the backend. Writes the raw `FleetProducts`
   * shape so `getFleetPremiumPackageFlag`'s `select` recomputes from it.
   *
   * Lives here because react-query access is confined to the hooks layer, and
   * because the panel previously wrote to a three-part key that no query
   * subscribed to, so the toggle silently did nothing.
   */
  const setFleetPremiumPackageFlag = (isPremium: boolean) => {
    queryClient.setQueryData<FleetProducts>(FLEET_PRODUCTS_QUERY_KEY, {
      products: [{ id: '', name: isPremium ? 'PREMIUM' : 'BASIC' }],
    });
  };

  return {
    FLEET_FLAG_QUERY_KEY,
    getCameraFleetFlag,
    getFleetPremiumPackageFlag,
    setFleetPremiumPackageFlag,
    hasCameraProDMSEvents,
  };
};

export default useCameraFleetFlag;
